import React from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-modal';
import cloneDeep from 'lodash.clonedeep';
import uniqBy from 'lodash.uniqby';
import orderBy from 'lodash.orderby';
import pick from 'lodash.pick';
import isNull from 'lodash.isnull';
import isEmpty from 'lodash.isempty';

import { Button, Checkbox, TabPanel, Tab } from '../../../components';
import FACILITY_MAP from './thsa-name-map.json';

const MODAL_FILTERS = [
	'facility_filter',
	'primary_payers',
	'secondary_payers',
	'discharge_dispositions',
];

export default class FilterModal extends React.Component {
	static propTypes = {
		isOpen: PropTypes.bool,
		onClose: PropTypes.func.isRequired,
		onSave: PropTypes.func.isRequired,
		isSaving: PropTypes.bool,
		filterOptions: PropTypes.object,
		currentFilter: PropTypes.object,
		filterBy: PropTypes.string,
	};

	state = {
		filterSelections: {},
	};

	componentDidUpdate(prevProps) {
		if (!prevProps.isOpen && this.props.isOpen) {
			// set up local selections
			const filterSelections = {
				...pick(this.props.filterOptions, MODAL_FILTERS),
				...cloneDeep(pick(this.props.currentFilter, MODAL_FILTERS)),
			};
			if (!Array.isArray(filterSelections.facility_filter)) {
				filterSelections.facility_filter = [];
			}
			if (!Array.isArray(filterSelections.discharge_dispositions)) {
				filterSelections.discharge_dispositions = [];
			}
			this.setState({ filterSelections });
		}
	}

	handleSave = () => {
		const { filterSelections } = this.state;
		let { primary_payers, secondary_payers, discharge_dispositions } =
			filterSelections;
		if (isEmpty(primary_payers)) {
			primary_payers = null;
		}
		if (isEmpty(secondary_payers)) {
			secondary_payers = null;
		}

		this.props.onSave({
			facility_filter: filterSelections.facility_filter.map((facility) => ({
				sending_facility: facility.sending_facility,
			})),
			primary_payers,
			secondary_payers,
			discharge_dispositions,
		});
	};

	handleValueToggle = ({ target }) => {
		this.setState({
			filterSelections: {
				...this.state.filterSelections,
				[target.name]: target.checked,
			},
		});
	};

	handleArrayValueToggle = ({ target }) => {
		const [name, prop] = target.name.split('.');
		if (target.checked) {
			this.setState((currState) => {
				const { filterSelections } = currState;
				if (!filterSelections[name]) {
					filterSelections[name] = [];
				}
				return {
					filterSelections: {
						...filterSelections,
						[name]: [...filterSelections[name], { [prop]: target.value }],
					},
				};
			});
		} else {
			this.setState((currState) => ({
				filterSelections: {
					...currState.filterSelections,
					[name]: currState.filterSelections[name].filter(
						(arrayVal) => arrayVal[prop] !== target.value
					),
				},
			}));
		}
	};

	handlePayerToggle = ({ target }) => {
		this.setState(({ filterSelections }) => {
			const { filterOptions } = this.props;
			let payers = cloneDeep(filterSelections[target.name]);
			if (target.checked) {
				payers[target.value] = cloneDeep(
					filterOptions[target.name][target.value]
				);
			} else {
				delete payers[target.value];
			}
			return {
				filterSelections: {
					...filterSelections,
					[target.name]: payers,
				},
			};
		});
	};

	handlePayerPlanToggle = ({ target }) => {
		this.setState(({ filterSelections }) => {
			const [payerType] = target.name.split('.');
			const [payer, plan] = target.value.split('|');
			const payers = cloneDeep(filterSelections[payerType]);
			if (target.checked) {
				payers[payer].push(plan);
			} else {
				const planIndex = payers[payer].indexOf(plan);
				payers[payer].splice(planIndex, 1);
				if (payers[payer].length === 0) {
					delete payers[payer];
				}
			}
			return {
				filterSelections: {
					...filterSelections,
					[payerType]: payers,
				},
			};
		});
	};

	handleClearAll = ({ target }) => {
		this.setState((currState) => ({
			filterSelections: {
				...currState.filterSelections,
				[target.name]: [],
			},
		}));
	};

	handleSelectAll = ({ target }) => {
		this.setState((currState) => ({
			filterSelections: {
				...currState.filterSelections,
				[target.name]: [...this.props.filterOptions[target.name]],
			},
		}));
	};

	handleTogglePayerAll = ({ target }) => {
		const filterSelections = cloneDeep(this.state.filterSelections);
		if (target.checked) {
			filterSelections[target.name] = null;
		} else {
			filterSelections[target.name] = cloneDeep(
				this.props.filterOptions[target.name]
			);
		}
		this.setState({ filterSelections });
	};

	render() {
		const { isOpen, onClose, isSaving, filterOptions, filterBy } = this.props;
		const { filterSelections } = this.state;

		return (
			<Modal
				isOpen={isOpen}
				contentLabel="ADT Filter Options"
				className="modal-dialog modal-lg filter-modal"
			>
				<div className="modal-content">
					<div className="modal-header">
						<div className="clearfix">
							<div className="pull-right">
								<button className="btn btn-link" onClick={onClose}>
									<em className="fa fa-lg fa-times" />
								</button>
							</div>
							<h3 className="modal-title">ADT Filter Options</h3>
						</div>
					</div>
					<div className="modal-body">
						<TabPanel>
							{filterBy == 'discharge_dispositions' && (
								<Tab
									id="discharge_dispositions"
									title={`Disposition Codes ${
										Array.isArray(filterSelections.discharge_dispositions)
											? `(${filterSelections.discharge_dispositions.length})`
											: ''
									}`}
								>
									<div className="row">
										<div className="col-sm-12">
											<Button
												title="Clear All"
												onClick={this.handleClearAll}
												info
												small
												name="discharge_dispositions"
											/>{' '}
											<Button
												title="Select All"
												onClick={this.handleSelectAll}
												info
												small
												name="discharge_dispositions"
											/>
										</div>
									</div>
									<div className="row">
										{filterOptions &&
											filterOptions.discharge_dispositions &&
											orderBy(
												filterOptions.discharge_dispositions,
												['code'],
												['asc']
											).map((disposition) => (
												<div className="col-sm-4" key={disposition.code}>
													<Checkbox
														checked={
															filterSelections.discharge_dispositions &&
															Boolean(
																filterSelections.discharge_dispositions.find(
																	(d) => d.code === disposition.code
																)
															)
														}
														onChange={this.handleArrayValueToggle}
														label={`${disposition.code} - ${disposition.description}`}
														name="discharge_dispositions.code"
														value={disposition.code}
													/>
												</div>
											))}
									</div>
								</Tab>
							)}
							{filterBy == 'facility_filter' && (
								<Tab
									id="facilities"
									title={`Facilities ${
										Array.isArray(filterSelections.facility_filter)
											? `(${filterSelections.facility_filter.length})`
											: ''
									}`}
								>
									<div className="row">
										<div className="col-sm-12">
											<Button
												title="Clear All"
												onClick={this.handleClearAll}
												info
												small
												name="facility_filter"
											/>{' '}
											<Button
												title="Select All"
												onClick={this.handleSelectAll}
												info
												small
												name="facility_filter"
											/>
										</div>
									</div>
									<div className="row">
										{filterOptions &&
											filterOptions.facility_filter &&
											uniqBy(
												orderBy(
													filterOptions.facility_filter,
													['sending_facility'],
													['asc']
												),
												'sending_facility'
											).map((facility) => (
												<div
													className="col-sm-4"
													key={facility.sending_facility}
												>
													<Checkbox
														checked={
															filterSelections.facility_filter &&
															Boolean(
																filterSelections.facility_filter.find(
																	(f) =>
																		f.sending_facility ===
																		facility.sending_facility
																)
															)
														}
														onChange={this.handleArrayValueToggle}
														label={
															FACILITY_MAP[facility.sending_facility] ||
															facility.sending_facility
														}
														name="facility_filter.sending_facility"
														value={facility.sending_facility}
													/>
												</div>
											))}
									</div>
								</Tab>
							)}
							{filterBy == 'primary_payers' && (
								<Tab id="primary_payers" title="Primary Payers and Plans">
									<div className="row">
										<fieldset>
											<div className="col-md-12">
												<Checkbox
													checked={isNull(filterSelections.primary_payers)}
													onChange={this.handleTogglePayerAll}
													label="All Primary Payers and Plans"
													name="primary_payers"
												/>
											</div>
										</fieldset>
										{filterOptions &&
											filterOptions.primary_payers &&
											Object.keys(filterOptions.primary_payers).map((payer) => (
												<React.Fragment key={payer}>
													<fieldset>
														<div className="col-md-12">
															<Checkbox
																checked={
																	filterSelections.primary_payers &&
																	Array.isArray(
																		filterSelections.primary_payers[payer]
																	)
																}
																onChange={this.handlePayerToggle}
																label={payer}
																name="primary_payers"
																value={payer}
																disabled={isNull(
																	filterSelections.primary_payers
																)}
															/>
														</div>
														<div className="col-md-10 col-md-offset-2">
															<div className="row">
																{filterSelections.primary_payers &&
																	filterOptions.primary_payers[payer] &&
																	filterOptions.primary_payers[payer].map(
																		(plan) => (
																			<div
																				className="col-sm-4"
																				key={`${payer}|${plan}`}
																			>
																				<Checkbox
																					checked={
																						Array.isArray(
																							filterSelections.primary_payers[
																								payer
																							]
																						) &&
																						filterSelections.primary_payers[
																							payer
																						].includes(plan)
																					}
																					onChange={this.handlePayerPlanToggle}
																					label={plan}
																					name="primary_payers.plan"
																					disabled={
																						!filterSelections.primary_payers[
																							payer
																						]
																					}
																					value={`${payer}|${plan}`}
																				/>
																			</div>
																		)
																	)}
															</div>
														</div>
													</fieldset>
												</React.Fragment>
											))}
									</div>
								</Tab>
							)}
							{filterBy == 'secondary_payers' && (
								<Tab id="secondaryPayers" title="Secondary Payers and Plans">
									<div className="row">
										<fieldset>
											<div className="col-md-12">
												<Checkbox
													checked={isNull(filterSelections.secondary_payers)}
													onChange={this.handleTogglePayerAll}
													label="All Secondary Payers and Plans"
													name="secondary_payers"
												/>
											</div>
										</fieldset>
										{filterOptions &&
											filterOptions.secondary_payers &&
											Object.keys(filterOptions.secondary_payers).map(
												(payer) => (
													<React.Fragment key={payer}>
														<fieldset>
															<div className="col-md-12">
																<Checkbox
																	checked={
																		filterSelections.secondary_payers &&
																		Array.isArray(
																			filterSelections.secondary_payers[payer]
																		)
																	}
																	onChange={this.handlePayerToggle}
																	label={payer}
																	name="secondary_payers"
																	value={payer}
																	disabled={isNull(
																		filterSelections.secondary_payers
																	)}
																/>
															</div>
															<div className="col-md-10 col-md-offset-2">
																<div className="row">
																	{filterSelections.secondary_payers &&
																		filterOptions.secondary_payers[payer] &&
																		filterOptions.secondary_payers[payer].map(
																			(plan) => (
																				<div
																					className="col-sm-4"
																					key={`${payer}|${plan}`}
																				>
																					<Checkbox
																						checked={
																							Array.isArray(
																								filterSelections
																									.secondary_payers[payer]
																							) &&
																							filterSelections.secondary_payers[
																								payer
																							].includes(plan)
																						}
																						onChange={
																							this.handlePayerPlanToggle
																						}
																						label={plan}
																						name="secondary_payers.plan"
																						disabled={
																							!filterSelections
																								.secondary_payers[payer]
																						}
																						value={`${payer}|${plan}`}
																					/>
																				</div>
																			)
																		)}
																</div>
															</div>
														</fieldset>
													</React.Fragment>
												)
											)}
									</div>
								</Tab>
							)}
						</TabPanel>
					</div>
					<div className="modal-footer">
						<Button
							title="Cancel"
							large
							onClick={onClose}
							disabled={isSaving}
						/>
						<Button
							title="Save"
							onClick={this.handleSave}
							disabled={isSaving}
							large
							primary
							className="ml"
						/>
					</div>
				</div>
			</Modal>
		);
	}
}
